import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  multiSelect,
  SelectList,
  Table,
} from "../../../../components/Functional";
import { LIST_TYPES } from "../../../../constants/listTypes";
import { CREATE_LIST as createListMutation } from "../../../../graphql/mutation/List";
import {
  FETCH_LIST as fetchList,
  FETCH_LISTS as fetchLists,
} from "../../../../graphql/query/List";
import {
  instanceFilterStore,
  updateInstanceNavigationStore,
} from "../../components/useInstanceFilters";
import Actions, {
  getInstanceDetailsUrl,
} from "./components/Actions";
import HeaderComponent from "../../../../components/ui/ListHeader";
import * as ImageSelectListSaved from "./components/ImageSelectListSaved";
import * as ImageSelectListWorking from "./components/ImageSelectListWorking";
import searchedInstances from "./components/SearchedInstances";
import * as SelectListSaved from "./components/SelectListSaved";
import * as SelectListWorking from "./components/SelectListWorking";
import InstanceGalleryModal from "../../components/InstanceGalleryModal";
import { formatValuePrice } from "../../components/InstanceGalleryModal/utils";
import InstanceStatus from "./components/Status";

const EditionNumber = ({ values }) => {
  const { edition, editionNumber } = values?.item || values || {};
  if (edition?.number === 1 || !editionNumber) return null;

  return (
    <div className="flex flex-col">
      <span className="text-base font-medium leading-5 text-black">{`${editionNumber}/${edition?.number}`}</span>
    </div>
  );
};

const SalePrice = ({ values }) => {
  const { value } = values?.item || values || {};
  const formatted = formatValuePrice(value);
  if (!formatted) return null;
  return (
    <span className="text-base font-medium leading-5 text-black">
      {formatted}
    </span>
  );
};

const toNumericId = value => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getInstanceIdFromValues = ({ values }) => {
  const itemId = toNumericId(values?.item?.id ?? values?.rawFields?.item?.id);
  if (itemId !== null) return itemId;

  const isListItemRow =
    values?.item || values?.rawFields?.item || values?.listId || values?.meta;
  if (isListItemRow) return null;

  return toNumericId(values?.id);
};

const List = props => {
  const { formik } = props;
  const navigate = useNavigate();
  const [galleryInstanceId, setGalleryInstanceId] = useState(null);
  const [lastViewedInstanceId, setLastViewedInstanceId] = useState(null);

  useEffect(() => {
    if (lastViewedInstanceId === null) return;
    const timer = setTimeout(() => setLastViewedInstanceId(null), 3000);
    return () => clearTimeout(timer);
  }, [lastViewedInstanceId]);

  const { data: { lists: { edges = [] } = {} } = {}, loading: loadingWorkingList } = useQuery(fetchLists(), {
    variables: {
      input: { typeId: LIST_TYPES.INSTANCE_WORKING },
    },
    fetchPolicy: "network-only",
  });
  const { data: { lists: { edges: imageListEdges = [] } = {} } = {}, loading: loadingImageWorkingList } =
    useQuery(fetchLists(), {
      variables: {
        input: { typeId: LIST_TYPES.INSTANCE_IMAGE_WORKING },
      },
      fetchPolicy: "network-only",
    });

  const [createList] = useMutation(createListMutation(), {
    refetchQueries: [
      {
        query: fetchLists(),
        variables: { input: {} },
        fetchPolicy: "network-only",
      },
    ],
    awaitRefetchQueries: true,
  });

  // Get the first list's ID if it exists (even if empty, the list object will have an id)
  const workingListId = edges?.[0]?.id ?? null;
  const imageWorkingListId = imageListEdges?.[0]?.id ?? null;

  // Ensure working lists exist - create them if they don't
  useEffect(() => {
    if (!loadingWorkingList && !workingListId) {
      createList({
        variables: {
          input: {
            typeId: LIST_TYPES.INSTANCE_WORKING,
            name: `select_list_working_${Date.now()}`,
          },
        },
      });
    }
  }, [loadingWorkingList, workingListId, createList]);

  useEffect(() => {
    if (!loadingImageWorkingList && !imageWorkingListId) {
      createList({
        variables: {
          input: {
            typeId: LIST_TYPES.INSTANCE_IMAGE_WORKING,
            name: `select_list_working_${Date.now()}`,
          },
        },
      });
    }
  }, [loadingImageWorkingList, imageWorkingListId, createList]);

  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const imageListIdParam = queryParams.get("imageListId");
  const workingListIdParam = queryParams.get("workingListId");
  const imageWorkingListIdParam = queryParams.get("imageWorkingListId");
  const listId = listIdParam ? +listIdParam : null;
  const imageListId = imageListIdParam ? +imageListIdParam : null;

  // Determine if listId/imageListId are working lists or saved lists
  const isWorkingList = listId && listId === workingListId;
  const isImageWorkingList = imageListId && imageListId === imageWorkingListId;

  // Fetch saved list names when viewing saved lists (not working lists)
  const { data: { list: savedList } = {}, loading: loadingSavedList } =
    useQuery(fetchList(), {
      variables: { id: listId },
      skip: !listId || isWorkingList,
    });

  const {
    data: { list: savedImageList } = {},
    loading: loadingSavedImageList,
  } = useQuery(fetchList(), {
    variables: { id: imageListId },
    skip: !imageListId || isImageWorkingList,
  });

  const { ids, select } = useMemo(() => {
    let select,
      ids = [];

    if (status === "imageSelectListWorking") {
      ids = Object.entries(formik.values.selectedImages?.ids || {}).map(
        ([_id, value]) => {
          return value?.item?.artwork?.id;
        },
      );
      select = formik?.values?.selectedImages?.select;
    } else {
      ids = Object.entries(formik.values.selected?.ids || {}).map(
        ([_id, value]) => {
          return value?.item?.id || value?.id;
        },
      );
      select = formik?.values?.selected?.select;
    }

    return {
      ids,
      select,
    };
  }, [formik.values, status]);

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    let shouldUpdate = false;

    // Remove workingListId if we're viewing a saved list (listId is present)
    if (listId && currentParams.has("workingListId")) {
      currentParams.delete("workingListId");
      shouldUpdate = true;
    }

    // Remove imageWorkingListId if we're viewing a saved image list (imageListId is present)
    if (imageListId && currentParams.has("imageWorkingListId")) {
      currentParams.delete("imageWorkingListId");
      shouldUpdate = true;
    }

    // Update workingListId if we're on selectListWorking tab, it's available, and no saved listId
    if (status === "selectListWorking" && workingListId && !listId) {
      if (currentParams.get("workingListId") !== String(workingListId)) {
        currentParams.set("workingListId", workingListId);
        shouldUpdate = true;
      }
    }

    // Update imageWorkingListId if we're on imageSelectListWorking tab, it's available, and no saved imageListId
    if (status === "imageSelectListWorking" && imageWorkingListId && !imageListId) {
      if (currentParams.get("imageWorkingListId") !== String(imageWorkingListId)) {
        currentParams.set("imageWorkingListId", imageWorkingListId);
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) {
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      // eslint-disable-next-line
      history.pushState(null, null, newUrl);
    }
  }, [workingListId, imageWorkingListId, status, listId, imageListId]);

  const multiSelectProps = {
    formik,
    label: "Instances",
    name: "selected",
    keyName: "id",
  };

  const actionProps = {
    formik,
    ids,
    imageListId,
    imageWorkingListId,
    listId,
    search: instanceFilterStore.get(),
    workingListId,
    onSuccess: () => formik?.resetForm(),
  };

  const captureNavigationSnapshot = useCallback(
    ({ count, edges, queryVariables }) => {
      if (status !== "all") return;

      updateInstanceNavigationStore({
        count,
        edges,
        queryVariables,
        statusContext: "all",
        listContext: {
          ...(listIdParam ? { listId: listIdParam } : {}),
          ...(imageListIdParam ? { imageListId: imageListIdParam } : {}),
          ...(workingListIdParam ? { workingListId: workingListIdParam } : {}),
          ...(imageWorkingListIdParam
            ? { imageWorkingListId: imageWorkingListIdParam }
            : {}),
        },
      });
    },
    [
      imageListIdParam,
      imageWorkingListIdParam,
      listIdParam,
      status,
      workingListIdParam,
    ],
  );

  const handleChange = value => {
    formik?.resetForm();
    let url = `/instance?status=${value}`;

    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
      // Preserve current image list context while switching entity list tabs
      if (imageListId) {
        url += `&imageListId=${imageListId}`;
      } else if (imageWorkingListId) {
        url += `&imageWorkingListId=${imageWorkingListId}`;
      }
    } else if (value === "imageSelectListWorking" || value === "imageSelectListSaved") {
      // Include imageListId if present (viewing/editing a saved image list), otherwise use imageWorkingListId
      if (imageListId) {
        url += `&imageListId=${imageListId}`;
      } else if (imageWorkingListId) {
        url += `&imageWorkingListId=${imageWorkingListId}`;
      }
      // Preserve current entity list context while switching image list tabs
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all" and other tabs, preserve saved list IDs (listId/imageListId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
      if (imageListId) {
        url += `&imageListId=${imageListId}`;
      }
    }

    navigate(url);
  };

  const GeneralTabActions = () => {
    // Determine target list IDs - use saved list IDs if present, otherwise use working list IDs
    const targetListId = listId && !isNaN(listId) ? listId : (workingListId || undefined);
    const targetImageListId = imageListId && !isNaN(imageListId) ? imageListId : (imageWorkingListId || undefined);

    const createListProps = {
      ...actionProps,
      // Use listId if present (saved list), otherwise use workingListId
      ...(targetListId ? { targetListId } : {}),
      onSuccess: () => (
        formik?.resetForm(),
        navigate(
          `/instance?status=selectListWorking${listId ? `&listId=${listId}` : ""}${imageListId ? `&imageListId=${imageListId}` : ""}`,
        )
      ),
      typeId: LIST_TYPES.INSTANCE_WORKING,
    };

    const createImageListProps = {
      ...actionProps,
      // Use imageListId if present (saved image list), otherwise use imageWorkingListId
      ...(targetImageListId ? { targetListId: targetImageListId } : {}),
      label: "Add Images to Select List",
      onSuccess: () => (
        formik?.resetForm(),
        navigate(
          `/instance?status=imageSelectListWorking${listId ? `&listId=${listId}` : ""}${imageListId ? `&imageListId=${imageListId}` : ""}`,
        )
      ),
      typeId: LIST_TYPES.INSTANCE_IMAGE_WORKING,
      isSearch: true,
      ...(select !== "all"
        ? { search: { id: { operator: "in", value: ids } } }
        : {}),
    };

    const createPrimaryImageListProps = {
      ...actionProps,
      // Use imageListId if present (saved image list), otherwise use imageWorkingListId
      ...(targetImageListId ? { targetListId: targetImageListId } : {}),
      label: "Add Primary Images to Select List",
      onSuccess: () => (
        formik?.resetForm(),
        navigate(
          `/instance?status=imageSelectListWorking${listId ? `&listId=${listId}` : ""}${imageListId ? `&imageListId=${imageListId}` : ""}`,
        )
      ),
      typeId: LIST_TYPES.INSTANCE_IMAGE_WORKING,
      primary: true,
      isSearch: true,
      ...(select !== "all"
        ? { search: { id: { operator: "in", value: ids } } }
        : {}),
    };

    return (
      <>
        <SelectList.CreateList {...createPrimaryImageListProps} />
        <SelectList.CreateList {...createImageListProps} />
        <SelectList.CreateList {...createListProps} />
      </>
    );
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      // { actions: GeneralTabActions, label: 'All Instances', name: 'all' },
      {
        actions: GeneralTabActions,
        label: "All Instances",
        name: "all",
        tableProps: searchedInstances(),
      },
      {
        actions: SelectListWorking.Actions,
        label: listId && !isWorkingList
          ? loadingSavedList
            ? "Select List (...)"
            : savedList?.name
              ? `Select List (${savedList.name})`
              : "Select List (Working)"
          : "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps({ listId, workingListId }),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps({ formik, listId }),
      },
      {
        actions: ImageSelectListWorking.Actions,
        label: imageListId && !isImageWorkingList
          ? loadingSavedImageList
            ? "Image Select List (...)"
            : savedImageList?.name
              ? `Image Select List (${savedImageList.name})`
              : "Image Select List (Working)"
          : "Image Select List (Working)",
        name: "imageSelectListWorking",
        tableProps: ImageSelectListWorking.tableProps({
          imageListId,
          imageWorkingListId,
          formik,
        }),
      },
      {
        actions: ImageSelectListSaved.Actions,
        label: "Image Select List (Saved)",
        name: "imageSelectListSaved",
        tableProps: ImageSelectListSaved.tableProps({ formik, imageListId }),
      },
    ],
  };
  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};
  const TabActions = tabActions;

  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          header?.name && status === "selectListWorking"
            ? Array.isArray(header?.name)
              ? header?.name?.map(item => `item.${item}`)
              : `item.${header?.name}`
            : header?.name,
      };
    });

  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "instances",
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        {
          label: "Artwork Image",
          name: "edition.artwork.imagesSummary.imgT",
          type: "image",
          onClick: fnProps => {
            const id = getInstanceIdFromValues(fnProps);
            if (typeof id === "number") setGalleryInstanceId(id);
          },
        },
        {
          label: "Reference",
          name: "name",
          type: "link",
          to: values => {
            const id = getInstanceIdFromValues(values);
            return typeof id === "number" ? getInstanceDetailsUrl(id) : "";
          },
        },
        {
          label: "Title",
          name: "edition.artwork.title",
          type: "label",
        },
        {
          label: "Artist Name",
          name: "edition.artwork",
          type: "custom",
          component: props => {
            const combinedName = props?.label?.artists
              ?.map(artist => artist.name)
              .join(",");
            return (
              <div
                title={combinedName}
                className={`overflow-hidden text-ellipsis text-base font-medium leading-5 text-black`}
                style={{ width: 140 }}
              >
                {combinedName}
              </div>
            );
          },
        },
        { label: "Year", name: "edition.year", type: "label" },
        {
          label: "Edition",
          name: "editionNumber",
          type: "custom",
          component: EditionNumber,
        },
        {
          label: "Sale Price",
          name: "value",
          type: "custom",
          component: SalePrice,
        },
        {
          label: "Status",
          name: "status.name",
          type: "custom",
          component: InstanceStatus,
        },
        {
          label: "",
          name: "action",
          type: "custom",
          component: props => (
            <Actions
              {...props}
              getInstanceIdFromValues={getInstanceIdFromValues}
            />
          ),
        },
      ],
    }),
    // inputs: [
    //   { label: 'Artist name', name: 'artistName', type: 'text', view: 'partial' },
    //   { label: 'Status', name: 'statusId', type: 'multi-select', view: 'partial', options: instanceStatus?.map(item => ({ label: item.name, value: item.id })) },
    // ],
    limit: 50,
    onQueryStateChange: captureNavigationSnapshot,
    rowClassName: (values) => {
      const rowId = toNumericId(values?.item?.id ?? values?.id);
      const isHighlighted = lastViewedInstanceId && rowId === lastViewedInstanceId;
      return `transition-colors duration-1000 ${isHighlighted ? "bg-yellow-50" : ""}`;
    },
    tabProps,
    ...tabTableProps,
  };

  return (
    <>
      <HeaderComponent
        leftText="All Instances"
        rightComponent={() => {
          return (
            <div className="flex gap-4">
              {TabActions ? <TabActions {...actionProps} /> : null}
            </div>
          );
        }}
      />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
      <InstanceGalleryModal
        instanceId={galleryInstanceId}
        isOpen={galleryInstanceId !== null}
        onClose={(viewedId) => {
          setLastViewedInstanceId(viewedId ?? galleryInstanceId);
          setGalleryInstanceId(null);
        }}
      />
    </>
  );
};

export default List;
export { getInstanceIdFromValues };
